<template>
  <div class="h-full flex flex-col">
    <Toolbar class="w-full mx-auto px-8 py-4 flex items-center justify-between z-10" />
    <div class="grow flex overflow-hidden" >
      <SlidesThumbnailPanel
        v-model:slides="parsedSlides"
        :currentSlideIndex="currentSlideIndex"
        @select-slide="selectSlide"
      />
      <div class="grow transition-all duration-300 ease-in-out">
        <div class="reveal h-full">
          <div class="slides">
            <section v-if="parsedSlides.length === 0">{{ t('slides_editor.components.slides_editor.no_slides') }}</section>
            <template v-else>
              <Slide
                v-for="(slide, index) in parsedSlides"
                :key="index"
                :slide="slide"
                :index="index"
                ref="editorContainer"
              />
            </template>
          </div>
        </div>
      </div>
      <SidePanel />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, provide, watch, nextTick, computed } from 'vue'
import Reveal from 'reveal.js'
import Slide from '@/features/slides_editor/components/slides/Slide.vue'
import Toolbar from '@/features/slides_editor/components/editor/Toolbar.vue'
import SidePanel from '@/features/slides_editor/components/editor/SidePanel.vue'
import SlidesThumbnailPanel from '@/features/slides_editor/components/editor/SlidesThumbnailPanel.vue'
import { useSlideHelpers } from '@/features/slides_editor/composables'
import { useSidePanelHelpers } from '@/features/slides_editor/composables/useSidePanelHelpers'
import { t } from '@/libs/i18n'
import { validateJson, safeJsonParse } from '@/helpers'
import { SLIDE_WIDTH, SLIDE_HEIGHT } from '@/features/slides_editor/config/constants/slides'

const props = defineProps({
  slides: {
    type: String,
    required: true,
    validator: validateJson
  },
  sparks: {
    type: String,
    required: true,
    validator: validateJson
  },
  school: {
    type: Object,
    default: () => undefined
  },
  prep_overview: {
    type: String,
    required: true
  },
  create_path: {
    type: String,
    required: true
  },
  prep_path: {
    type: String,
    required: true
  },
  layouts: {
    type: String,
    required: true,
    validator: validateJson
  },
  lesson_settings: {
    type: String,
    required: false,
    validator: validateJson
  },
  categories: {
    type: String,
    required: true,
    validator: validateJson
  },
  year_levels: {
    type: String,
    required: true,
    validator: validateJson
  },
  lesson_covers: {
    type: String,
    required: true,
    validator: validateJson
  },
  feature_flags: {
    type: Object,
    default: () => {}
  }
})

const lessonSettings = ref({})
const parsedSlides = ref([])
const parsedLayouts = ref([])
const parsedCategories = ref([])
const parsedYearLevels = ref([])
const parsedSparks = ref([])
const parsedLessonCovers = ref([])
const currentEditor = ref(null)
const saveStatusMessage = ref('')
const isSaving = ref(false)
const errors = ref({})
const editorContainer = ref(null)
const reveal = ref(null)
const currentSlideIndex = ref(0)
const prepOverview = ref(props.prep_overview)
const { getConfig, addSlide, removeSlide, updateSlides } = useSlideHelpers(parsedSlides, reveal)
const {
  prependToStack,
  pushToStack,
  popFromStack,
  removeNonSharedFromStack,
  replaceInStack,
  activePanel,
  closeSidePanel
} = useSidePanelHelpers()

const currentSlide = computed(() => parsedSlides.value[currentSlideIndex.value])

provide('reveal', reveal)
provide('updateSlides', updateSlides)
provide('parsedSlides', parsedSlides)
provide('parsedSparks', parsedSparks)
provide('currentEditor', currentEditor)
provide('saveStatusMessage', saveStatusMessage)
provide('isSaving', isSaving)
provide('addSlide', addSlide)
provide('createPath', props.create_path)
provide('removeSlide', removeSlide)
provide('parsedLayouts', parsedLayouts)
provide('currentSlide', currentSlide)
provide('prepOverview', prepOverview)
provide('errors', errors)
provide('featureFlags', props.feature_flags)
provide('school', props.school)

const selectSlide = (index) => {
  currentSlideIndex.value = index
  reveal.value?.slide(index)
}

const connectLessonSettingsButton = () => {
  const lessonSettingsButton = document.querySelector('#lesson-settings-button')
  if (lessonSettingsButton) {
    lessonSettingsButton.addEventListener('click', () => {
      pushToStack({
        content: {
          component: 'LessonSettingsForm',
          title: t('slides_editor.components.slides_editor.lesson_settings'),
          width: '343px',
          props: {
            lessonSettings: lessonSettings.value,
            categories: parsedCategories.value,
            yearLevels: parsedYearLevels.value,
            updatePath: lessonSettings.value.update_path,
            lessonCovers: parsedLessonCovers.value
          }
        },
        shared: true
      })
    })
  }
}

const connectEditPrepButton = () => {
  const editPrepButton = document.querySelector('#editor-prep-button')
  if (editPrepButton) {
    editPrepButton.addEventListener('click', () => {
      // Toggle: close if PrepForm is already open, otherwise open it
      if (activePanel.value?.content?.component === 'PrepForm') {
        popFromStack()
      } else {
        pushToStack({
          content: {
            component: 'PrepForm',
            title: t('slides_editor.components.slides_editor.prep_overview'),
            width: '540px',
            props: {
              updateUrl: props.prep_path
            }
          },
          shared: true
        })
      }
    })
  }
}

const connectPreviewStudentViewButton = () => {
  const previewStudentViewButton = document.querySelector('#preview-student-view-button')
  if (previewStudentViewButton) {
    previewStudentViewButton.addEventListener('click', (event) => {
      event.preventDefault()

      pushToStack({
        content: {
          component: 'StudentViewPreview',
          title: t('slides_editor.components.slides_editor.student_view'),
          width: '600px',
          props: {
            frameUrl: previewStudentViewButton.href
          }
        },
        shared: true
      })
    })
  }
}

onMounted(() => {
  parsedSlides.value = safeJsonParse(props.slides, [], 'slides')
  parsedLayouts.value = safeJsonParse(props.layouts, [], 'layouts')
  parsedSparks.value = safeJsonParse(props.sparks, [], 'sparks')
  lessonSettings.value = safeJsonParse(props.lesson_settings, {}, 'lesson settings')
  parsedCategories.value = safeJsonParse(props.categories, [], 'categories')
  parsedYearLevels.value = safeJsonParse(props.year_levels, [], 'year levels')
  parsedLessonCovers.value = safeJsonParse(props.lesson_covers, [], 'lesson covers')

  connectLessonSettingsButton()
  connectEditPrepButton()
  connectPreviewStudentViewButton()

  const onSlideChanged = () => {
    const slide = parsedSlides.value[reveal.value.getIndices().h]
    const config = getConfig(slide, props.feature_flags)

    if (config.side_panel.fixed && activePanel.value?.shared) {
      removeNonSharedFromStack()
      prependToStack({
        content: config.side_panel.content({ slide }),
        fixed: true
      })
    } else if (config.side_panel.fixed) {
      replaceInStack({
        content: config.side_panel.content({ slide }),
        fixed: true
      })
    } else {
      removeNonSharedFromStack()
    }

    currentSlideIndex.value = reveal.value.getIndices().h
  }

  reveal.value = new Reveal({
    hash: false,
    width: SLIDE_WIDTH,
    height: SLIDE_HEIGHT,
    controls: false,
    help: false
  })

  reveal.value.initialize().then(() => {
    reveal.value.on('slidechanged', onSlideChanged)
    onSlideChanged()
  })
})

onUnmounted(() => {
  closeSidePanel()
})

watch(parsedSlides, () => {
  nextTick(() => {
    if (reveal.value && typeof reveal.value.sync === 'function') {
      reveal.value.sync()
    }
  })
})
</script>
